import { FC, SyntheticEvent, useEffect, useRef, useState } from "react";
import { MdsDropdownContentList, MdsDropdownItemKind } from "@/design-system/components/dropdown";
import { MdsChip } from "@/design-system/components/input/types";
import { css } from "@/domains/emotion";
import {
  MDSChip,
  MDSTextField,
  MDSTextFieldProps,
  MDSTextFieldSlotName,
  MDSTextFieldVariant,
} from "@/mds";
import styled from "@emotion/styled";
import { Popover } from "react-aria-components";
import { MdsDropdownContent } from "@/design-system/components/dropdown/MdsDropdownContent";

export interface MdsChipInputProps extends Omit<MDSTextFieldProps, "onSubmit"> {
  getChips: (value: string) => Promise<MdsChip[]>;
  chips: MdsChip[];
  setChips: React.Dispatch<React.SetStateAction<MdsChip[]>>;
  onSubmit: (chips: MdsChip[]) => void;
  chipIconSize?: number;
}

/**
 * @deprecated - Use the newer MDS components instead.
 *
 * - If something doesn't exist yet, please create a new MDS component and
 *   have it reviewed by the team!
 *
 * - If something needs to hook into our `stores`, add it to the /components directory.
 *
 * - If something is a one-off; please colocate it with the component/page that
 *   needs it.
 *
 * - If you have questions, please ask in the #hub-eng-web-team Slack channel.
 */
export const MdsChipInput: FC<MdsChipInputProps> = ({
  isDisabled,
  getChips,
  chips,
  setChips,
  onSubmit,
  placeholder,
  autoFocus,
  chipIconSize,
}) => {
  const inputRef = useRef<HTMLInputElement>(null);
  const [value, setValue] = useState<string>("");
  const [isHoveringOverAvailableChips, setIsHoveringOverAvailableChips] = useState<boolean>(false);
  const [keyboardSelectedIndex, setKeyboardSelectedIndex] = useState<number>(-1);

  const [availableChips, setAvailableChips] = useState<MdsChip[]>([]);

  useEffect(() => {
    const fetchAvailableChips = async (value: string) => {
      const chips = await getChips(value);
      setAvailableChips(chips);
    };
    fetchAvailableChips(value);
  }, [getChips, value]);

  useEffect(() => {
    if (availableChips.length > 0) setKeyboardSelectedIndex(0);
    else setKeyboardSelectedIndex(-1);
  }, [availableChips]);

  const contentList: MdsDropdownContentList = {
    items: availableChips
      .sort((a, b) => b.orderByString.localeCompare(a.orderByString))
      .map((chip, index) => ({
        id: chip.id,
        kind: MdsDropdownItemKind.Button,
        iconKind: chip.icon ? () => chip.icon?.(chipIconSize ?? 24) : undefined,
        label: chip.label,
        content: () => (
          <div className={columnStyles}>
            <div>{chip.label}</div>
            {chip.secondaryLabel && <div className={secondaryStyles}>{chip.secondaryLabel}</div>}
          </div>
        ),
        isSelected: index === keyboardSelectedIndex,
        scrollIntoView: index === keyboardSelectedIndex,
        onClick: () => {
          handleSelectChip(chip);
          setValue("");
        },
      })),
  };

  const handleSelectChip = (chip: MdsChip) => {
    setChips(chips => {
      const hasChip = chips.some(c => c.id === chip.id);
      if (hasChip) return chips;
      return [...chips, chip];
    });
    setIsHoveringOverAvailableChips(false);
    inputRef.current?.focus();
  };

  const handleRemoveChip = (chip: MdsChip) => {
    setChips(chips => chips.filter(c => c.id !== chip.id));
  };

  const handleInputChange = (value: string) => {
    if (value.endsWith(",") || value.endsWith(" ")) {
      const trimmedValue = value.substring(0, value.length - 1).trim();
      const matchingChips = availableChips.filter(chip =>
        chip.searchLabels?.includes(trimmedValue)
      );
      if (matchingChips.length === 1) {
        handleSelectChip(matchingChips[0]);
        setValue("");
        return;
      }
    }
    setValue(value);
  };

  const handleKeydown = (e: React.KeyboardEvent<HTMLDivElement>) => {
    switch (e.key) {
      case "Enter": {
        if (value.length === 0 && chips.length > 0) {
          e.preventDefault();
          onSubmit(chips);
          return;
        }
        break;
      }
      case "Backspace": {
        if (value.length === 0 && chips.length > 0) {
          e.preventDefault();
          handleRemoveChip(chips[chips.length - 1]);
          return;
        }
        break;
      }
    }
    if (availableChips.length === 0 || isHoveringOverAvailableChips) return;

    switch (e.key) {
      case "ArrowUp": {
        e.preventDefault();
        setKeyboardSelectedIndex(
          (keyboardSelectedIndex - 1 + availableChips.length) % availableChips.length
        );
        break;
      }
      case "ArrowDown": {
        e.preventDefault();
        setKeyboardSelectedIndex((keyboardSelectedIndex + 1) % availableChips.length);
        break;
      }
      case "Enter": {
        e.preventDefault();
        handleSelectChip(availableChips[keyboardSelectedIndex]);
        setValue("");
        break;
      }
    }
  };

  const handleDropdownHover = ({
    isHovering,
    itemId,
  }: {
    isHovering?: boolean;
    itemId?: string;
  }) => {
    setIsHoveringOverAvailableChips(!!isHovering || !!itemId);
    if (isHovering) setKeyboardSelectedIndex(-1);
  };

  const triggerRef = useRef<HTMLDivElement>(null);

  const stopEventPropagation = (event: SyntheticEvent) => {
    event.stopPropagation();
  };

  return (
    <Container ref={triggerRef}>
      <StyledTextField
        variant={MDSTextFieldVariant.Emphasis}
        placeholder={chips.length ? undefined : placeholder}
        onKeyDown={handleKeydown}
        value={value}
        onChange={handleInputChange}
        autoFocus={autoFocus}
        isDisabled={isDisabled}
        ref={inputRef}
      >
        {chips.map(chip => (
          <MDSChip
            key={chip.id}
            slot={MDSTextFieldSlotName.LeftAddon}
            icon={chip.icon?.(chipIconSize)}
            onRemove={() => handleRemoveChip(chip)}
            isDisabled={isDisabled}
          >
            {chip.label}
          </MDSChip>
        ))}
      </StyledTextField>
      <StyledPopover
        triggerRef={triggerRef}
        isOpen={contentList.items.length > 0}
        placement="bottom start"
        isNonModal
        onPointerDown={stopEventPropagation}
        onMouseDown={stopEventPropagation}
        onClick={stopEventPropagation}
      >
        <MdsDropdownContent contentList={contentList} onHover={handleDropdownHover} />
      </StyledPopover>
    </Container>
  );
};

const columnStyles = css({
  display: "flex",
  flexDirection: "column",
  alignItems: "flex-start",
});

const secondaryStyles = css({
  fontSize: "13px",
  color: "rgb(104, 110, 125)",
});

const Container = styled.div`
  flex: 1;
`;

const StyledTextField = styled(MDSTextField)`
  display: flex;
  flex-direction: row;
  flex-wrap: wrap;
  gap: ${({ theme }) => theme.spacing["1"]};
  padding: ${({ theme }) => `${theme.spacing["1"]} ${theme.spacing["5"]}`};
  min-height: 40px;
`;

const StyledPopover = styled(Popover)`
  background: ${({ theme }) => theme.colors.surface.menu};
  border-radius: ${({ theme }) => theme.borderRadius.large};
  box-shadow: ${({ theme }) => theme.shadows.surface};
  border: ${({ theme }) => `1px solid ${theme.colors.border.default}`};
  overflow: scroll;
`;
